import { useMemo } from 'react';
import { useActor } from '@xstate/react';
import { CrmIntegrationType } from '@alucio/aws-beacon-amplify/src/models';
import { useAppSettings } from 'src/state/context/AppSettings';
import { useTenant } from 'src/state/redux/selector/tenant';
import ActiveUser from 'src/state/global/ActiveUser';
import * as Types from 'src/state/machines/CRM/crmMachineTypes';
import { CRMUtil } from 'src/state/machines/CRM/util';
import { CRM_NON_AUTH_SESSION_ERRORS } from 'src/state/machines/CRM/constants';
import {
  getCRMAutomaticLoginPreference,
  isCRMAutomaticLoginAvailable,
} from 'src/state/machines/CRM/automaticLogin';
import useFeatureFlags from 'src/hooks/useFeatureFlags/useFeatureFlags';
import { CRMAccountSyncCheckpoint } from 'src/classes/CRM/CRMIndexedDBTypes';

export const ERROR_REFRESH_TOKEN_MESSAGES = 'Your CRM session has expired. Please log in to CRM and submit again'

const crmUtil = new CRMUtil();

export interface CRM_STATUS {
  canUseMeetingCRMFields: boolean;
  displayCRMConnectButton: boolean;
  displayDashboardCRMSection: boolean;
  displayCRMConnectHeaderButton: boolean;
  isCRMSyncing: boolean;
  isCRMConfigEnabled: boolean;
  hasCRMData: boolean;
  syncStatus: Types.CRMSyncStatus;
  connectionStatus: Types.CRMConnectionStatus;
  availabilityStatus: Types.CRMAvailabilityStatus;
  crmIntegrationType?: CrmIntegrationType | keyof typeof CrmIntegrationType;
  isInSessionError: boolean;
  isAutomaticLoginEnabled: boolean;
  accountSync?: CRMAccountSyncCheckpoint;
  retryAccountSync: () => void;
}

export const useRefreshToken = () => {
  const { crmMachine } = useAppSettings()
  const [state, send] = useActor(crmMachine);

  const onExpiredTokenError = (error?): void => {
    send({
      type: 'CLEAR_SESSION_ON_REFRESH_TOKEN_ERROR',
      data: error,
    })
  }

  const refreshToken = async () => {
    try {
      const { crmSession } = state.context

      if (!crmSession) return
      const result = await crmUtil.refreshTokenCRM(crmSession.accessToken, crmSession?.refreshToken, false)
      if (!result) {
        onExpiredTokenError();
        return
      }

      send({
        type: 'SET_ACCESS_TOKEN',
        data: {
          accessToken: result.accessToken,
        },
      })
    } catch (e) {
      onExpiredTokenError(e);
      console.error('Error refreshing token', e)
      throw new ErrorEvent('', {
        error: [ERROR_REFRESH_TOKEN_MESSAGES],
        message: ERROR_REFRESH_TOKEN_MESSAGES,
      });
    }
  }

  return refreshToken
}

export const useCRMStatus = () => {
  const { crmMachine } = useAppSettings()
  const [state, send] = useActor(crmMachine);
  const tenant = useTenant(ActiveUser.user!.tenantId);
  const automaticLoginFlag = useFeatureFlags('BEAC_7921_crm_automatic_login');

  const CRMType = tenant?.tenant.config.crmIntegration?.crmIntegrationType
  const crmIntegration = tenant?.tenant.config.crmIntegration;

  const isRefreshingDataTag = state.hasTag('REFRESH_DATA')
  const isRefreshingTokenTag = state.hasTag('REFRESH_TOKEN')
  const isCRMConfigEnabled = state.context.crmAvailabilityStatus === Types.CRMAvailabilityStatus.ENABLED;
  const isCRMSyncing = [Types.CRMSyncStatus.RE_SYNCING, Types.CRMSyncStatus.SYNCING]
    .includes(state.context.crmSyncStatus) ||
    isRefreshingDataTag || isRefreshingTokenTag;
  const sessionError = state.context.sessionError;
  const isInSessionError = !!sessionError && !CRM_NON_AUTH_SESSION_ERRORS.includes(sessionError);
  const connectionStatus = isInSessionError
    ? Types.CRMConnectionStatus.DISCONNECTED
    : state.context.connectionStatus;
  const isConnected = connectionStatus === Types.CRMConnectionStatus.CONNECTED;
  const isError = state.context.crmSyncStatus === Types.CRMSyncStatus.ERROR
  const isAutomaticLoginEnabled = isCRMAutomaticLoginAvailable({
    crmIntegration,
    isFeatureFlagEnabled: automaticLoginFlag,
  }) && getCRMAutomaticLoginPreference();

  const cond = useMemo(() => ({
    isCRMConfigEnabled,
    syncStatus: state.context.crmSyncStatus,
    isCRMSyncing,
    hasCRMData: isCRMConfigEnabled && !!state.context.lastSyncedAt,
    canUseMeetingCRMFields: isCRMConfigEnabled && !isCRMSyncing && !!state.context.lastSyncedAt,
    connectionStatus,
    availabilityStatus: state.context.crmAvailabilityStatus,
    isInSessionError,
    isAutomaticLoginEnabled,
    accountSync: state.context.accountSync,
    displayCRMConnectHeaderButton: isCRMConfigEnabled && (isConnected || isError || isCRMSyncing),
    displayCRMConnectButton: state.context.crmAvailabilityStatus === Types.CRMAvailabilityStatus.ENABLED &&
      (state.context.crmSyncStatus === Types.CRMSyncStatus.ERROR ||
        [Types.CRMConnectionStatus.DISCONNECTED, Types.CRMConnectionStatus.DISCONNECTING]
          .includes(connectionStatus)),
  }), [
    isConnected,
    isCRMConfigEnabled,
    isCRMSyncing,
    isError,
    isInSessionError,
    isAutomaticLoginEnabled,
    connectionStatus,
    state.context.crmSyncStatus,
    state.context.crmAvailabilityStatus,
    state.context.lastSyncedAt,
    state.context.accountSync,
  ])
  return useMemo<CRM_STATUS>(() => ({
    syncStatus: cond.syncStatus,
    displayCRMConnectHeaderButton: cond.displayCRMConnectHeaderButton,
    displayCRMConnectButton: cond.displayCRMConnectButton,
    displayDashboardCRMSection: cond.isCRMConfigEnabled && !cond.hasCRMData && !cond.isCRMSyncing,
    hasCRMData: cond.hasCRMData,
    isCRMConfigEnabled: cond.isCRMConfigEnabled,
    isCRMSyncing: cond.isCRMSyncing,
    canUseMeetingCRMFields: cond.canUseMeetingCRMFields,
    connectionStatus: cond.connectionStatus,
    availabilityStatus: cond.availabilityStatus,
    crmIntegrationType: CRMType,
    isInSessionError: cond.isInSessionError,
    isAutomaticLoginEnabled: cond.isAutomaticLoginEnabled,
    accountSync: cond.accountSync,
    retryAccountSync: () => send({ type: 'RETRY_ACCOUNT_SYNC' }),
  }), [cond, send])
}
